import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import BiotechOutlinedIcon from '@mui/icons-material/BiotechOutlined';
import FaceRetouchingNaturalIcon from '@mui/icons-material/FaceRetouchingNatural';
import {
  Button,
  CircularProgress,
  ClickAwayListener,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  Input,
  Tooltip,
} from '@mui/material';
import Box from '@mui/material/Box';
import CardActions from '@mui/material/CardActions';
import CardHeader from '@mui/material/CardHeader';
import Divider from '@mui/material/Divider';
import { alpha, lighten, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import CopyableText from 'components/atoms/CopyableText';
import DownloadDisplayNameFeatures from 'components/atoms/DownloadDisplayNameFeatures';
import { DownloadFilesModal, FileToDownload } from 'components/atoms/DownloadFilesModal';
import { getHighlightedParts, HighlightedPart } from 'components/atoms/HighlightedText';
import { useOpenStudyReportRoute } from 'components/Copilot/hooks/useOpenStudyReportRoute';
import JobsControl from 'components/Pages/Jobs/JobsControl';
import { specimenMap } from 'components/StudyDashboard/ProceduresPage/ProcedureCard/ProcedureCardUtils';
import { shouldShowStudyExport } from 'components/StudyDashboard/studyActionAvailability';
import { ResultsMode } from 'interfaces/experimentResults';
import { Permission } from 'interfaces/permissionOption';
import { Study } from 'interfaces/study';
import { curry, isEmpty, map } from 'lodash';
import moment from 'moment';
import { stringify } from 'qs';
import React, { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { encodeQueryParams, JsonParam, StringParam } from 'use-query-params';
import escapeHtml from 'utils/escapeHtml';
import { exportFeatureNames } from 'utils/exports';
import { useFormatBracketsOptions } from 'utils/formatBrackets/formatBracketsOptions';
import { useAllCancerTypes } from 'utils/queryHooks/uiConstantsHooks';
import { useCurrentLabId } from 'utils/useCurrentLab';
import { useCurrentUser } from 'utils/useCurrentUser';
import { useEncodedFilters } from 'utils/useEncodedFilters';
import { useMutationWithErrorSnackbar } from 'utils/useMutationWithErrorSnackbar';
import { usePermissions } from 'utils/usePermissions';
import useStudyMutations from 'utils/useStudyMutations';
import CardContentEditable from './CardContentEditable';
import { CardDataBox, StyledCard, TypeIconBox } from './CardStyles';
import CardTypography from './CardTypography';
import { discoverCellTableArtifactFiles } from './StudyExportMenu/cellTableArtifactDiscovery';
import StudyExportMenu from './StudyExportMenu/StudyExportMenu';
import StudyLastUpload from './StudyLastUpload';
import StudySettingsControl from './StudySettings/StudySettingsControl';

interface Props {
  isUploadSummaryLoading?: boolean;
  isUploadSummaryUnavailable?: boolean;
  lastUploadedAt?: string;
  matchedFields?: string[];
  searchQuery?: string;
  study: Study;
}

const backgroundColorCancerType = '#8a8f9226';
type ViewTransitionCapableDocument = Document & {
  startViewTransition?: (callback: () => void) => void;
};

const StudyCard: React.FunctionComponent<React.PropsWithChildren<Props>> = ({
  study,
  lastUploadedAt,
  isUploadSummaryLoading = false,
  isUploadSummaryUnavailable = false,
  matchedFields = [],
  searchQuery,
}) => {
  const theme = useTheme();
  const formatBracketsOptions = useFormatBracketsOptions({
    addStain: false,
    studyId: study.id,
  });
  const { labId } = useCurrentLabId();
  const [orchestrationId, setOrchestrationId] = useState('');
  const [isDownloadDisplayNameFeatures, setIsDownloadDisplayNameFeatures] = useState(false);
  const [isOrchestrationIdForDownloadCellsTableDisplay, setIsOrchestrationIdForDownloadCellsTableDisplay] =
    useState(false);

  const [inEditMode, setInEditMode] = useState(false);

  const { queryParams } = useEncodedFilters();

  const { allCancerTypes } = useAllCancerTypes();
  const navigate = useNavigate();
  const openStudyReportRoute = useOpenStudyReportRoute();
  const cancerType = allCancerTypes?.[study?.cancerTypeId];
  const specimen = cancerType ? specimenMap[cancerType.displayName] : null;
  const cancerTypeBaseColor = cancerType?.color ? cancerType.color : backgroundColorCancerType;
  const cancerTypeColor =
    theme.palette.mode === 'dark'
      ? alpha(lighten(cancerTypeBaseColor, cancerType?.color ? 0.12 : 0.28), cancerType?.color ? 0.96 : 0.84)
      : cancerTypeBaseColor;
  const fallbackIndicationColor =
    theme.palette.mode === 'dark'
      ? alpha(theme.palette.secondary.light, 0.22)
      : alpha(theme.palette.secondary.light, 0.55);
  const navigateToStudyDashboard = useCallback(
    curry((e: React.MouseEvent<HTMLElement>) => {
      e.stopPropagation();

      const encodedQuery = encodeQueryParams(
        { filters: JsonParam, labId: StringParam, resultsMode: StringParam },
        {
          filters: { ...queryParams.filters, studyId: study.id },
          labId,
          resultsMode: queryParams.resultsMode,
        }
      );

      const navigateToProcedures = () => {
        navigate({
          pathname: `/procedures`,
          search: `?${stringify({ ...encodedQuery })}`,
        });
      };

      const documentWithViewTransition = document as ViewTransitionCapableDocument;

      if (documentWithViewTransition.startViewTransition) {
        documentWithViewTransition.startViewTransition(navigateToProcedures);
      } else {
        navigateToProcedures();
      }
    }),
    [navigate, study.id]
  );

  const { studyMutation, handleFieldSave } = useStudyMutations(study, {
    onCloseSnackbar: () => setInEditMode(false),
  });

  const onStudyClick = (e: React.MouseEvent<HTMLElement>) => {
    if (!inEditMode) {
      navigateToStudyDashboard(e);
    } else {
      setInEditMode(false);
    }
  };

  const [cellsTableFiles, setCellsTableFiles] = useState<FileToDownload[] | undefined>(undefined);
  const cellsTableDiscoveryController = React.useRef<AbortController>();
  const cellsTableCasesParams = {
    studyId: study.id,
    filters: { orchestrationId, studyId: study.id },
    labId,
    resultsMode: queryParams.resultsMode,
  };

  React.useEffect(() => () => cellsTableDiscoveryController.current?.abort(), []);

  const discoverCellsTableFiles = (signal: AbortSignal) =>
    discoverCellTableArtifactFiles(cellsTableCasesParams, signal).then((artifacts) =>
      map(artifacts, (artifact) => ({
        id: artifact.id,
        url: artifact.urlForGetRequest,
        title: artifact.title,
        caseId: artifact.caseId,
        slideId: artifact.slideId,
        orchestrationId: artifact.orchestrationId,
      }))
    );

  const discoverCellsTableMutation = useMutationWithErrorSnackbar({
    mutationKey: ['discoverCellsTableFiles', cellsTableCasesParams],
    mutationFn: () => {
      cellsTableDiscoveryController.current?.abort();
      const controller = new AbortController();
      cellsTableDiscoveryController.current = controller;
      return discoverCellsTableFiles(controller.signal);
    },
    mutationDescription: 'discover cells table files',
    onSuccess: setCellsTableFiles,
  });

  const refreshSelectedCellsTableFiles = async (selectedFiles: FileToDownload[], signal: AbortSignal) => {
    const refreshedFiles = await discoverCellsTableFiles(signal);
    const refreshedFilesById = new Map(map(refreshedFiles, (file) => [file.id, file]));
    const refreshedSelection = map(selectedFiles, (file) => refreshedFilesById.get(file.id)).filter(
      Boolean
    ) as FileToDownload[];
    if (refreshedSelection.length !== selectedFiles.length) {
      throw new Error('One or more selected cell table files are no longer available.');
    }
    return refreshedSelection;
  };

  const { hasPermission } = usePermissions();
  const { userId: currentUserId } = useCurrentUser();

  const hasSlides = study.slidesCount > 0;
  const canOpenStudyCopilot =
    hasPermission(Permission.AccessAnalysisStudio) && hasPermission(Permission.ViewNucleaiCopilot);
  const canDownloadFeatureNamesCsv = hasSlides && hasPermission(Permission.CanDownloadFeatureNamesCsv);
  const canDownloadCellsTable = hasSlides && hasPermission(Permission.DownloadCellsTable);
  const canEditStudyName = hasPermission(Permission.EditStudyNameAndDescription);
  const canEditStudySettings = hasPermission(Permission.ViewStudySettings) || study.studyAdmins.includes(currentUserId);
  const highlightUnderlineColor = alpha(theme.palette.primary.main, theme.palette.mode === 'light' ? 0.9 : 0.95);

  const handleOpenStudyCopilot = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    openStudyReportRoute({ activeStudyId: study.id, keepDrawerOpen: true, labId });
  };

  const renderHighlightedText = (value: string, normalize: boolean = false) => {
    const parts = getHighlightedParts(value, searchQuery, { normalize });

    return map(parts, (part: HighlightedPart, index: number) =>
      part.highlighted ? (
        <Box
          key={`${value}-${index}`}
          component="mark"
          sx={{
            backgroundColor: 'transparent',
            color: 'inherit',
            textDecorationLine: 'underline',
            textDecorationColor: highlightUnderlineColor,
            textDecorationThickness: '0.18em',
            textUnderlineOffset: '0.18em',
            textDecorationSkipInk: 'none',
            fontWeight: 600,
          }}
        >
          {part.text}
        </Box>
      ) : (
        <React.Fragment key={`${value}-${index}`}>{part.text}</React.Fragment>
      )
    );
  };

  const getHighlightedHtml = (value: string, normalize: boolean = false) => {
    const parts = getHighlightedParts(value, searchQuery, { normalize });

    return parts
      .map((part) =>
        part.highlighted
          ? `<mark style="background-color: transparent; color: inherit; text-decoration-line: underline; text-decoration-color: ${highlightUnderlineColor}; text-decoration-thickness: 0.18em; text-underline-offset: 0.18em; text-decoration-skip-ink: none; font-weight: 600;">${escapeHtml(
              part.text
            )}</mark>`
          : escapeHtml(part.text)
      )
      .join('');
  };

  return (
    <ClickAwayListener onClickAway={() => setInEditMode(false)}>
      <StyledCard
        data-test-class={`study-card-${study.id}`}
        variant="outlined"
        onClick={onStudyClick}
        sx={{
          backgroundColor: theme.palette.background.paper,
          position: 'relative',
          '&:hover .study-card-navigation-arrow': {
            opacity: 1,
          },
        }}
        className="study-card"
        editMode={inEditMode}
      >
        <Box
          className="study-card-navigation-arrow"
          sx={{
            position: 'absolute',
            right: 24,
            top: '50%',
            transform: 'translateY(-50%)',
            display: 'flex',
            alignItems: 'center',
            color: 'text.disabled',
            pointerEvents: 'none',
            opacity: 0,
            transition: 'opacity 120ms linear',
          }}
        >
          <ArrowForwardIcon sx={{ fontSize: 18 }} />
        </Box>
        <CardHeader
          sx={{
            width: '100%',
            pr: 3,
            '& .MuiCardHeader-content': {
              minWidth: 0,
              pr: 2,
            },
            '& .MuiCardHeader-action': {
              alignSelf: 'center',
              display: 'flex',
              alignItems: 'center',
              m: 0,
              ml: 2,
              mr: 0,
            },
          }}
          title={
            <CardContentEditable
              disabled={!canEditStudyName}
              displayHtml={matchedFields.includes('Name') ? getHighlightedHtml(study.name) : undefined}
              savedValue={study.name}
              onSave={(newValue: string) => handleFieldSave('name', newValue)}
              onEditModeChange={setInEditMode}
            />
          }
          subheader={
            <>
              <Grid container spacing={1} alignItems="center">
                {cancerType && (
                  <>
                    <Grid item>
                      <Typography variant="body1">{cancerType?.displayName}</Typography>
                    </Grid>
                    <Grid item>
                      <Divider orientation="vertical" variant="middle" flexItem sx={{ margin: '0 10px' }} />
                    </Grid>
                  </>
                )}

                <Grid item>{study.date && moment(study.date).format('ll')}</Grid>
              </Grid>
              <Grid item>
                <CopyableText textForCopy={study.id} variant="caption">
                  {matchedFields.includes('UUID') ? renderHighlightedText(study.id, true) : study.id}
                </CopyableText>
              </Grid>
            </>
          }
          avatar={
            specimen ? (
              <Box
                sx={{
                  borderRadius: '50%',
                  p: theme.palette.mode === 'dark' ? 0.5 : 0,
                  backgroundColor:
                    theme.palette.mode === 'dark' ? alpha(theme.palette.common.white, 0.05) : 'transparent',
                }}
              >
                <TypeIconBox
                  sx={{
                    backgroundColor: cancerTypeColor,
                    '& img': {
                      filter: theme.palette.mode === 'dark' ? 'saturate(1.12) brightness(1.08)' : 'none',
                    },
                  }}
                >
                  <img src={specimen.img} alt={specimen.abbreviation} />
                </TypeIconBox>
              </Box>
            ) : (
              <TypeIconBox
                sx={{
                  backgroundColor: fallbackIndicationColor,
                  color: theme.palette.mode === 'dark' ? theme.palette.secondary.light : theme.palette.secondary.main,
                }}
              >
                <BiotechOutlinedIcon fontSize="small" />
              </TypeIconBox>
            )
          }
          action={
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1, width: '100%' }}>
              {canOpenStudyCopilot && (
                <Tooltip title="Copilot" placement="top">
                  <Box>
                    <IconButton color="inherit" size="small" onClick={handleOpenStudyCopilot}>
                      <FaceRetouchingNaturalIcon />
                    </IconButton>
                  </Box>
                </Tooltip>
              )}

              <CardActions sx={{ p: 0, ml: 'auto', mr: 0, display: 'flex', alignItems: 'center' }}>
                {hasPermission(Permission.ViewJobs) && <JobsControl studyId={study?.id} />}

                {canEditStudySettings && <StudySettingsControl study={study} />}
              </CardActions>
            </Box>
          }
        ></CardHeader>
        <Typography
          data-cy="study-description"
          variant="body1"
          component="div"
          color="text.secondary"
          sx={{
            maxWidth: '80%',
            minHeight: 72,
            px: 2,
            py: 1.5,
            mb: 2.5,
          }}
        >
          <CardContentEditable
            disabled={!canEditStudyName}
            displayHtml={matchedFields.includes('Description') ? getHighlightedHtml(study.description) : undefined}
            savedValue={study.description}
            onSave={(newValue: string) => handleFieldSave('description', newValue)}
            onEditModeChange={setInEditMode}
            placeholderDisplay="hover"
            placeholder="Add description..."
            style={{ width: '100%', minHeight: 40, padding: '6px 10px' }}
          />
        </Typography>

        {matchedFields.length > 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', px: 2, pb: 2 }}>
            Matched in {matchedFields.join(', ')}
          </Typography>
        )}

        <CardActions sx={{ display: 'flex', alignItems: 'self-start', padding: '0 16px' }}>
          <CardDataBox>
            <CardTypography>Cases</CardTypography>
            <CardTypography color="text.primary" bold>
              {study.casesCount}
            </CardTypography>
          </CardDataBox>
          <CardDataBox>
            <CardTypography>Slides</CardTypography>
            <CardTypography color="text.primary" bold>
              {study.slidesCount}
            </CardTypography>
          </CardDataBox>
          <StudyLastUpload
            uploadedAt={lastUploadedAt}
            isLoading={isUploadSummaryLoading}
            isUnavailable={isUploadSummaryUnavailable}
          />
          {study.externalStudyId && (
            <CardDataBox>
              <CardTypography>Study ID</CardTypography>
              <CardTypography bold color="text.primary">
                {matchedFields.includes('Study ID')
                  ? renderHighlightedText(study.externalStudyId, true)
                  : study.externalStudyId}
              </CardTypography>
            </CardDataBox>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', flex: 1 }}>
            {canDownloadFeatureNamesCsv && (
              <CardDataBox>
                <Grid container direction="column">
                  <Grid item>
                    <DownloadDisplayNameFeatures
                      nodeId={`download-feature-names-${study.id}`}
                      buttonColor="inherit"
                      buttonVariant="text"
                      onClick={() => {
                        setIsDownloadDisplayNameFeatures(
                          (prevIsDownloadDisplayNameFeatures) => !prevIsDownloadDisplayNameFeatures
                        );
                      }}
                      isStudyExists={true}
                    />
                  </Grid>
                  {isDownloadDisplayNameFeatures && (
                    <Grid item container justifyContent="space-between">
                      <Grid item>
                        <FormControl fullWidth variant="standard">
                          <Input
                            id={`download-feature-names-orchestration-id-input-${study.id}`}
                            sx={{ width: '100px' }}
                            aria-describedby="standard-weight-helper-text"
                            inputProps={{
                              'aria-label': 'weight',
                            }}
                            value={orchestrationId}
                            onChange={(event) => {
                              setOrchestrationId(event.target.value);
                            }}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                            }}
                          />
                          <FormHelperText id="standard-weight-helper-text">Orchestration Id</FormHelperText>
                        </FormControl>
                      </Grid>
                      <Grid item>
                        <Button
                          id={`download-feature-names-${study.id}-for-orchestration`}
                          disabled={isEmpty(orchestrationId) || formatBracketsOptions.isLoading}
                          color="inherit"
                          size="small"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            exportFeatureNames({
                              studyId: study.id,
                              orchestrationId,
                              context: { ...formatBracketsOptions, orchestrationId },
                            });
                            setIsDownloadDisplayNameFeatures(false);
                          }}
                        >
                          Download
                        </Button>
                      </Grid>
                    </Grid>
                  )}
                </Grid>
              </CardDataBox>
            )}
            {canDownloadCellsTable && (
              <CardDataBox>
                <Grid container direction="column">
                  <Grid item>
                    <Button
                      id={`download-cells-table-${study.id}`}
                      color="inherit"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();

                        if (queryParams.resultsMode === ResultsMode.Manual) {
                          setIsOrchestrationIdForDownloadCellsTableDisplay(
                            (prevIsOrchestrationIdForDownloadCellsTableDisplay) =>
                              !prevIsOrchestrationIdForDownloadCellsTableDisplay
                          );
                        } else {
                          discoverCellsTableMutation.mutate();
                        }
                      }}
                      endIcon={discoverCellsTableMutation.isLoading && <CircularProgress size={16} />}
                    >
                      Download Cells Table
                    </Button>
                  </Grid>
                  <DownloadFilesModal
                    open={Boolean(cellsTableFiles)}
                    onClose={() => setCellsTableFiles(undefined)}
                    filesToDownload={cellsTableFiles || []}
                    dialogTitle="Download Cells Table"
                    emptyMessage="No cell table files are available for this study."
                    zipConfiguration={{
                      archiveName: `cell_tables_study_${study.id}_whole-study.zip`,
                      refreshFiles: refreshSelectedCellsTableFiles,
                    }}
                  />
                  {isOrchestrationIdForDownloadCellsTableDisplay && (
                    <Grid item container justifyContent="space-between">
                      <Grid item>
                        <FormControl fullWidth variant="standard">
                          <Input
                            id={`download-cells-table-orchestration-id-input-${study.id}`}
                            sx={{ width: '100px' }}
                            aria-describedby="standard-weight-helper-text"
                            inputProps={{
                              'aria-label': 'weight',
                            }}
                            value={orchestrationId}
                            onChange={(event) => {
                              setOrchestrationId(event.target.value);
                            }}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                            }}
                          />
                          <FormHelperText id="standard-weight-helper-text">Orchestration Id</FormHelperText>
                        </FormControl>
                      </Grid>
                      <Grid item>
                        <Button
                          id={`download-cells-table-${study.id}-for-orchestration`}
                          disabled={isEmpty(orchestrationId)}
                          color="inherit"
                          size="small"
                          endIcon={discoverCellsTableMutation.isLoading && <CircularProgress size={16} />}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            discoverCellsTableMutation.mutate();
                            setIsOrchestrationIdForDownloadCellsTableDisplay(false);
                          }}
                        >
                          Download
                        </Button>
                      </Grid>
                    </Grid>
                  )}
                </Grid>
              </CardDataBox>
            )}
            {shouldShowStudyExport(study) && (
              <CardDataBox>
                <Box sx={{ display: 'flex', alignItems: 'center', minHeight: 40 }}>
                  <StudyExportMenu
                    skipPreloadingStudyStains
                    study={study}
                    disabled={studyMutation.isLoading || !study.allowExport}
                    triggerButtonColor="secondary"
                    triggerButtonVariant="contained"
                  />
                </Box>
              </CardDataBox>
            )}
          </Box>
        </CardActions>
      </StyledCard>
    </ClickAwayListener>
  );
};

export default StudyCard;
